import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Constant } from '../shared/Constant';
import { Helpers } from '../helpers/Helpers';

@Injectable({
  providedIn: 'root'
})
export class PageVisitService {
  private initialized = false;
  private lastLoggedKey: string = null;
  private lastLoggedAt = 0;
  private readonly dedupeMs = 5000;
  private readonly ignoredPrefixes = [
    '/login',
    '/forget-password',
    '/set-password',
    '/verify',
    '/not-found'
  ];

  constructor(
    private router: Router,
    private http: HttpClient,
    private helper: Helpers
  ) {}

  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.recordVisit(event.urlAfterRedirects);
    });
  }

  recordVisit(url: string): void {
    if (!this.helper.userId) {
      return;
    }

    const pagePath = this.sanitizePath(url);
    if (!pagePath || this.shouldIgnore(pagePath)) {
      return;
    }

    const projectNum = this.extractProjectId(url);
    const dedupeKey = `${pagePath}|${projectNum || ''}`;
    const now = Date.now();
    if (dedupeKey === this.lastLoggedKey && now - this.lastLoggedAt < this.dedupeMs) {
      return;
    }

    this.lastLoggedKey = dedupeKey;
    this.lastLoggedAt = now;

    this.http.post(Constant.webapi + 'PageVisits', {
      pagePath,
      pageTitle: document.title || null,
      projectNum
    }).subscribe(
      () => {},
      () => {
        // Ignore logging failures so navigation is never blocked.
      }
    );
  }

  private shouldIgnore(pagePath: string): boolean {
    return this.ignoredPrefixes.some(prefix =>
      pagePath === prefix || pagePath.startsWith(prefix + '/')
    );
  }

  private extractProjectId(url: string): number | null {
    const raw = url || '';
    const pathPart = raw.split('?')[0].split('#')[0] || '';
    const queryPart = raw.includes('?') ? raw.split('?')[1].split('#')[0] : '';

    const params = new URLSearchParams(queryPart);
    const fromQuery = params.get('projectId');
    if (fromQuery && /^\d+$/.test(fromQuery)) {
      return Number(fromQuery);
    }

    const patterns = [
      /\/admin\/projects\/(\d+)(?:\/|$)/,
      /\/admin\/project-boq\/(\d+)(?:\/|$)/,
      /\/admin\/project-users\/(\d+)(?:\/|$)/,
      /\/admin\/options\/(\d+)(?:\/|$)/,
      /\/admin\/(\d+)\/(?:boqcompare|option-detail|snapshotcompare)/
    ];

    for (let i = 0; i < patterns.length; i++) {
      const match = pathPart.match(patterns[i]);
      if (match) {
        return Number(match[1]);
      }
    }

    return null;
  }

  private sanitizePath(url: string): string {
    const path = (url || '/').split('?')[0].split('#')[0] || '/';
    return path
      .replace(/\/\d+/g, '/:id')
      .replace(
        /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
        '/:id'
      );
  }
}
